// src/components/winback/WinbackStatsCharts.jsx
import React from 'react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { useWinbackStats } from '../../hooks/useWinbackStats';

const COLORS = {
    high: '#EF4444',    // Red
    medium: '#F59E0B',  // Yellow
    low: '#10B981'      // Green
};

const SEGMENT_COLORS = {
    'Campeones': '#10B981',
    'Clientes Fieles': '#3B82F6',
    'En Riesgo': '#F59E0B',
    'No Pueden Perderse': '#F97316',
    'A Punto de Dormir': '#EF4444',
    'Perdidos': '#6B7280',
    'Sin Compras': '#8B5CF6'
};

const WinbackStatsCharts = ({ marcaId }) => {
    const { stats, loading } = useWinbackStats(marcaId);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-purple-600 mb-4"></div>
                    <p className="text-gray-600">Cargando estadísticas...</p>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500">No hay datos disponibles</p>
            </div>
        );
    }

    // Preparar datos para gráfico de segmentos
    const segmentData = Object.entries(stats.bySegment || {}).map(([segment, count]) => ({
        name: segment,
        value: count,
        fill: SEGMENT_COLORS[segment] || '#6B7280'
    }));

    // Preparar datos para gráfico de urgencia
    const urgencyData = Object.entries(stats.byUrgency || {}).map(([urgency, count]) => ({
        name: urgency === 'high' ? 'Alta' : urgency === 'medium' ? 'Media' : 'Baja',
        value: count,
        fill: COLORS[urgency]
    }));

    return (
        <div className="space-y-8">
            {/* Gráfico de Conversión por Segmento */}
            <div>
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-4">
                    Conversión por Segmento RFM
                </h4>
                {segmentData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={segmentData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={120} />
                            <Tooltip
                                formatter={(value) => [`${value} clientes`, 'Cantidad']}
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                                {segmentData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-64 flex items-center justify-center text-gray-400">
                        No hay datos de segmentos
                    </div>
                )}
            </div>

            {/* Gráfico de Distribución por Urgencia */}
            <div>
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-4">
                    Distribución por Urgencia
                </h4>
                {urgencyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={urgencyData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {urgencyData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value) => [`${value} clientes`, 'Cantidad']}
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-64 flex items-center justify-center text-gray-400">
                        No hay datos de urgencia
                    </div>
                )}
            </div>

            {/* Métricas adicionales */}
            <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">
                        Tiempo Promedio de Conversión
                    </p>
                    <p className="text-2xl font-bold text-purple-900 mt-2">
                        {stats.avgDaysToConvert ? `${stats.avgDaysToConvert.toFixed(1)} días` : 'N/A'}
                    </p>
                </div>

                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wider">
                        Efectividad
                    </p>
                    <p className="text-2xl font-bold text-green-900 mt-2">
                        {stats.converted && stats.sent ? `${((stats.converted / stats.sent) * 100).toFixed(1)}%` : '0%'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WinbackStatsCharts;

